import { DEFAULT_SEGMENTATION_OPERATOR } from '../constants/remoteConfig.constant';
import type {
  FeatureFlagMultiKey,
  FeatureFlagObject,
  Segmentation,
  SegmentationContext,
} from '../types/featureFlag.types';

export function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isFeatureFlagObject(value: unknown): value is FeatureFlagObject {
  return isPlainRecord(value) && typeof value.enabled === 'boolean';
}

export function isFeatureFlagMultiKey(value: unknown): value is FeatureFlagMultiKey {
  if (!isPlainRecord(value)) return false;
  const subValues = Object.values(value);
  if (subValues.length === 0) return false;
  return subValues.every(isFeatureFlagObject);
}

function matchProperty(
  expected: string[] | undefined,
  actual: string | undefined
): boolean | null {
  if (expected === undefined) return null;
  if (!actual) return false;
  return expected.includes(actual);
}

export function matchesSegmentation(
  segmentation: Segmentation,
  context: SegmentationContext
): boolean {
  const operator = segmentation.operator ?? DEFAULT_SEGMENTATION_OPERATOR;

  const checks = [
    matchProperty(segmentation.company_id, context.company_id),
    matchProperty(segmentation.user_id, context.user_id),
    matchProperty(segmentation.country, context.country),
    matchProperty(segmentation.email, context.email),
    matchProperty(segmentation.currency, context.currency),
  ].filter((result): result is boolean => result !== null);

  if (checks.length === 0) return false;

  return operator === 'AND' ? checks.every(Boolean) : checks.some(Boolean);
}

/**
 * Resuelve el `enabled` de un FeatureFlagObject aplicando la regla:
 *
 *   result = enabled || (segmentation existe && contexto listo && usuario matchea)
 *
 * Reglas adicionales:
 * - Si la flag declara `segmentation` y el contexto aún no está listo
 *   (`isReady === false`), el resultado es `false` aunque `enabled` venga `true`.
 *   Esto evita habilitar features para usuarios cuyo KYC no terminó de cargarse,
 *   porque sin contexto no podemos garantizar que la segmentación se evalúe
 *   correctamente.
 * - Si la flag NO declara `segmentation`, el contexto es irrelevante: se respeta
 *   `enabled` tal como venga de Remote Config.
 */
export function resolveEnabled(
  flag: FeatureFlagObject,
  context: SegmentationContext
): boolean {
  const hasSegmentation = flag.segmentation !== undefined;

  if (hasSegmentation && !context.isReady) return false;
  if (flag.enabled) return true;
  if (!hasSegmentation) return false;

  return matchesSegmentation(flag.segmentation as Segmentation, context);
}

/**
 * Aplica la regla de segmentación al valor de un feature flag.
 *
 * Contrato: el output preserva exactamente el shape del input. Solo se muta el
 * atributo `enabled` (en el objeto raíz o en cada sub-objeto si es multi-llave).
 *
 * - `{ enabled, ... }`: se devuelve el objeto con `enabled` resuelto, todo lo
 *   demás intacto. `{ enabled }` puro vuelve como `{ enabled: bool }` (sin
 *   auto-unwrap a boolean — preservamos el shape para compatibilidad con
 *   futuros atributos).
 * - `{ key1: { enabled, ... }, key2: { ... }, ... }`: se recorre cada sub-objeto
 *   y se aplica la regla individualmente.
 * - Cualquier otra forma (booleans, shapes sin `enabled`): se devuelve sin
 *   transformar. `getFeatureFlag` está pensado para FFs que ya respetan el
 *   contrato; las flags legacy se siguen leyendo por `getRemoteConfigValue`.
 */
export function resolveFeatureFlag<T>(value: T, context: SegmentationContext): T {
  if (isFeatureFlagObject(value)) {
    return { ...value, enabled: resolveEnabled(value, context) } as T;
  }

  if (isFeatureFlagMultiKey(value)) {
    const result: FeatureFlagMultiKey = {};
    for (const [key, sub] of Object.entries(value)) {
      result[key] = { ...sub, enabled: resolveEnabled(sub, context) };
    }
    return result as T;
  }

  return value;
}
